// Plan 24b, Task 2.6 — Tile Effect Popup (Screen 12)
//
// Passive overlay that translates ServerMessage game-events into floating popup
// triggers (gold gain, hazard HP/key loss, HP heal, item gain, trophy gain) and a
// brief styled fallback landing flash for tiles without a dedicated effect event.
// Rendered above the board HUD (z-index 300).
//
// Event → FloatingPopup mapping:
//   EconomyChangeEvent(resource='keys') → key-gain/key-loss +/-Δ keys
//   Hazard DamageEvent + optional damage_drop/death_drop → hp-damage -HP / -keys
//   HealEvent(amount>0) → hp-heal +amount HP
//   PlayerReceivedItemEvent → item-received "New item received!"
//   EconomyChangeEvent, resource='trophies', newValue > oldValue → trophy-gain +Δ trophy
//   EconomyChangeEvent, resource='trophies', newValue <= oldValue → (ignored)
//
// Landing fallback flash:
//   LandingEvent on tiles without a dedicated effect event → renders a styled
//   <div class="tile-name-flash"> with readable copy, centred at viewport midpoint.
//   Removal is scheduled via gsapRegistry.create('tile-effect:name-flash') +
//   tl.call(removeFn, undefined, 1.5) — NOT bare setTimeout.
//
import {
  createFloatingPopup,
  type FloatingPopupTrigger,
} from '@client/ui/components/floating-popup';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import type { GsapTimelineHandle } from '@client/renderer/vfx/gsap-registry';
import type { ServerMessage } from '@shared/protocol';

const DAMAGE_POPUP_LIFETIME_SECONDS = 2.5;
const EFFECT_EVENT_TILE_TYPES = new Set([
  'gold',
  'hazard',
  'heal',
  'item',
  'weapons_cache',
  'treasure_chest',
  'soul_anchor',
]);

const FLASH_FONT_FAMILY =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";
const FLASH_FONT_SIZE = 'var(--typography-size-h3, 28px)';
const FLASH_TEXT_SHADOW = 'var(--typography-shadow-heading, 0 2px 8px rgba(0,0,0,0.70))';
const HAZARD_KEY_DROP_REASONS = new Set(['damage_drop', 'death_drop']);
const POPUP_STACK_GAP_PX = 38;
const SEEN_EVENT_LIMIT = 256;

interface LandingContext {
  readonly tileType: string;
  effectShown: boolean;
}

interface PendingHazardPopup {
  readonly playerId: string;
  readonly hpLost: number;
  readonly screenX: number;
  readonly screenY: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createTileEffectPopupScreen(
  ctx: ScreenMountContext<BoardMountPayload>,
): ScreenHandle {
  const { gsapRegistry, host, payload } = ctx;

  let disposed = false;
  let nameFlashHandle: GsapTimelineHandle | null = null;
  let nameFlashEl: HTMLElement | null = null;
  const landingContexts = new Map<string, LandingContext>();
  let pendingHazardPopup: PendingHazardPopup | null = null;
  const seenEventKeys = new Set<string>();
  const seenEventOrder: string[] = [];
  let floatingPopupActive = false;
  let nameFlashActive = false;
  let reportedBoardEffectActive = false;

  // ── Root element ─────────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-tile-effect-popup';
  root.setAttribute('data-screen-id', 'tile-effect-popup');
  root.style.cssText = ['position: fixed', 'inset: 0', 'z-index: 300', 'pointer-events: none'].join(
    '; ',
  );

  host.appendChild(root);

  // ── Floating popup component ──────────────────────────────────────────────

  const floatingPopupHandle = createFloatingPopup({
    gsapRegistry,
    mount: root,
    lifetimeSeconds: DAMAGE_POPUP_LIFETIME_SECONDS,
    stackGapPx: POPUP_STACK_GAP_PX,
    maxVisiblePerAnchor: 8,
    stackOrder: 'newest-at-anchor',
    overflowMode: 'drop-oldest',
    onActivityChange: (active) => {
      floatingPopupActive = active;
      syncBoardEffectActivity();
    },
  });

  // ── Subscribe to ServerMessages ────────────────────────────────────────────

  const unsubscribe = payload.subscribe(handleMessage);

  // ─────────────────────────────────────────────────────────────────────────────
  // Event handler
  // ─────────────────────────────────────────────────────────────────────────────

  function handleMessage(msg: ServerMessage): void {
    if (disposed) return;
    if (msg.type !== 'event') return;

    const evt = msg.event;
    if (isDuplicateEvent(evt)) return;

    if (pendingHazardPopup && !isHazardKeyDropForPendingPopup(msg)) {
      flushPendingHazardPopup();
    }

    if (evt.type === 'economy_change') {
      if (evt.resource === 'keys') {
        const keyDelta = evt.newValue - evt.oldValue;
        if (applyHazardKeyDrop(evt.playerId, evt.reason, evt.oldValue - evt.newValue)) {
          return;
        }
        if (HAZARD_KEY_DROP_REASONS.has(evt.reason)) {
          return;
        }
        if (keyDelta === 0) {
          return;
        }

        const popupPosition = notificationPositionForPlayer(evt.playerId);
        triggerPopupNow({
          kind: keyDelta > 0 ? 'key-gain' : 'key-loss',
          text: formatResourcePopupText('keys', keyDelta),
          screenX: popupPosition.screenX,
          screenY: popupPosition.screenY,
        });
        const landingContext = landingContexts.get(evt.playerId);
        const shouldMarkEffectShown = !(
          landingContext?.tileType === 'hazard' && evt.reason === 'dropped_keys_pickup'
        );
        if (landingContext && !landingContext.effectShown && shouldMarkEffectShown) {
          landingContext.effectShown = true;
        }
        return;
      }

      if (evt.resource === 'trophies' && evt.newValue > evt.oldValue) {
        const popupPosition = notificationPositionForPlayer(evt.playerId);
        triggerPopupNow({
          kind: 'trophy-gain',
          text: formatResourcePopupText('trophies', evt.newValue - evt.oldValue),
          screenX: popupPosition.screenX,
          screenY: popupPosition.screenY,
        });
        return;
      }

      // trophies, newValue <= oldValue → ignored
      return;
    }

    if (evt.type === 'damage') {
      if (evt.amount <= 0) return;
      const landingContext = landingContexts.get(evt.playerId);
      if (
        evt.source === 'hazard_tile' &&
        landingContext?.tileType === 'hazard' &&
        !landingContext.effectShown
      ) {
        const popupPosition = notificationPositionForPlayer(evt.playerId);
        pendingHazardPopup = {
          playerId: evt.playerId,
          hpLost: evt.amount,
          screenX: popupPosition.screenX,
          screenY: popupPosition.screenY,
        };
        landingContext.effectShown = true;
        return;
      }

      const popupPosition = notificationPositionForPlayer(evt.playerId);
      if (evt.source === 'item_toxic_cloud') {
        triggerPopupNow({
          kind: 'hp-damage',
          text: `-${evt.amount} HP`,
          screenX: popupPosition.screenX,
          screenY: popupPosition.screenY,
        });
        return;
      }

      triggerPopupNow({
        kind: 'hp-damage',
        text: `-${evt.amount} HP`,
        screenX: popupPosition.screenX,
        screenY: popupPosition.screenY,
      });
      return;
    }

    if (evt.type === 'heal') {
      if (evt.amount <= 0) return;
      const landingContext = landingContexts.get(evt.playerId);
      const popupPosition = notificationPositionForPlayer(evt.playerId);
      triggerPopupNow({
        kind: 'hp-heal',
        text: `+${evt.amount} HP`,
        screenX: popupPosition.screenX,
        screenY: popupPosition.screenY,
      });
      if (landingContext && !landingContext.effectShown) {
        landingContext.effectShown = true;
      }
      return;
    }

    if (evt.type === 'player_received_item') {
      const landingContext = landingContexts.get(evt.playerId);
      const popupPosition = notificationPositionForPlayer(evt.playerId);
      triggerPopupNow({
        kind: 'item-received',
        text: 'New item received!',
        screenX: popupPosition.screenX,
        screenY: popupPosition.screenY,
      });
      if (landingContext && !landingContext.effectShown) {
        landingContext.effectShown = true;
      }
      return;
    }

    if (evt.type === 'landing') {
      landingContexts.set(evt.playerId, {
        tileType: evt.tileType,
        effectShown: false,
      });
      if (!EFFECT_EVENT_TILE_TYPES.has(evt.tileType)) {
        const { screenX, screenY } = notificationPositionForPlayer(evt.playerId);
        showTileNameFlash(formatTileFallbackText(evt.tileType), screenX, screenY);
      }
      return;
    }

    if (evt.type === 'turn_end') {
      landingContexts.delete(evt.playerId);
    }
  }

  function isHazardKeyDropForPendingPopup(msg: ServerMessage): boolean {
    if (msg.type !== 'event') return false;
    const event = msg.event;
    if (!pendingHazardPopup) return false;
    return (
      event.type === 'economy_change' &&
      event.resource === 'keys' &&
      event.playerId === pendingHazardPopup.playerId &&
      HAZARD_KEY_DROP_REASONS.has(event.reason) &&
      event.newValue < event.oldValue
    );
  }

  function isDuplicateEvent(evt: Extract<ServerMessage, { type: 'event' }>['event']): boolean {
    const key = eventKey(evt);
    if (seenEventKeys.has(key)) return true;
    seenEventKeys.add(key);
    seenEventOrder.push(key);
    while (seenEventOrder.length > SEEN_EVENT_LIMIT) {
      const expired = seenEventOrder.shift();
      if (expired != null) seenEventKeys.delete(expired);
    }
    return false;
  }

  function eventKey(evt: Extract<ServerMessage, { type: 'event' }>['event']): string {
    switch (evt.type) {
      case 'economy_change':
        return [
          evt.timestamp,
          evt.type,
          evt.playerId,
          evt.resource,
          evt.oldValue,
          evt.newValue,
          evt.reason,
        ].join(':');
      case 'damage':
        return [
          evt.timestamp,
          evt.type,
          evt.playerId,
          evt.amount,
          evt.newHp,
          evt.source,
          evt.sourcePlayerId ?? '',
        ].join(':');
      case 'heal':
        return [evt.timestamp, evt.type, evt.playerId, evt.amount, evt.newHp, evt.source].join(':');
      case 'player_received_item':
        return [evt.timestamp, evt.type, evt.playerId, evt.itemId, evt.source].join(':');
      case 'landing':
        return [evt.timestamp, evt.type, evt.playerId, evt.nodeId, evt.tileType].join(':');
      case 'movement_step':
        return [
          evt.timestamp,
          evt.type,
          evt.playerId,
          evt.fromNodeId,
          evt.toNodeId,
          evt.stepIndex,
          evt.totalSteps,
        ].join(':');
      default:
        return JSON.stringify(evt);
    }
  }

  function notificationPositionForPlayer(playerId: string): { screenX: number; screenY: number } {
    return payload.getPlayerNotificationAnchor?.(playerId) ?? viewportCenter();
  }

  function viewportCenter(): { screenX: number; screenY: number } {
    return {
      screenX: window.innerWidth / 2,
      screenY: window.innerHeight / 2,
    };
  }

  function applyHazardKeyDrop(playerId: string, reason: string, keysLost: number): boolean {
    if (!pendingHazardPopup) return false;
    if (pendingHazardPopup.playerId !== playerId) return false;
    if (!HAZARD_KEY_DROP_REASONS.has(reason)) return false;
    if (keysLost <= 0) return false;

    flushPendingHazardPopup(keysLost);
    return true;
  }

  function flushPendingHazardPopup(keysLost = 0): void {
    if (!pendingHazardPopup) return;
    const pending = pendingHazardPopup;
    pendingHazardPopup = null;

    triggerPopupNow({
      kind: 'hp-damage',
      text: keysLost > 0 ? `-${pending.hpLost} HP / -${keysLost} keys` : `-${pending.hpLost} HP`,
      screenX: pending.screenX,
      screenY: pending.screenY,
    });
  }

  function triggerPopupNow(opts: FloatingPopupTrigger): void {
    floatingPopupHandle.trigger(opts);
  }

  function syncBoardEffectActivity(): void {
    const active = floatingPopupActive || nameFlashActive;
    if (reportedBoardEffectActive === active) return;
    reportedBoardEffectActive = active;
    payload.onBoardEffectActivityChange?.(active);
  }

  function setNameFlashActive(active: boolean): void {
    if (nameFlashActive === active) return;
    nameFlashActive = active;
    syncBoardEffectActivity();
  }

  function formatResourcePopupText(resource: 'keys' | 'trophies', delta: number): string {
    const sign = delta > 0 ? '+' : '-';
    const amount = Math.abs(delta);
    if (resource === 'keys') return `${sign}${amount} keys`;
    return `${sign}${amount} ${amount === 1 ? 'trophy' : 'trophies'}`;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Tile name flash
  // ─────────────────────────────────────────────────────────────────────────────

  function showTileNameFlash(text: string, screenX: number, screenY: number): void {
    if (disposed) return;

    // Dispose any existing flash — also remove its DOM element since cancelling
    // the GSAP handle prevents the tl.call removal callback from running.
    if (nameFlashHandle) {
      nameFlashHandle.dispose();
      nameFlashHandle = null;
    }
    if (nameFlashEl?.parentNode) {
      nameFlashEl.parentNode.removeChild(nameFlashEl);
      nameFlashEl = null;
    }

    const flashEl = document.createElement('div');
    flashEl.className = 'tile-name-flash';
    flashEl.textContent = text;
    flashEl.style.cssText = [
      'position: absolute',
      `left: ${screenX}px`,
      `top: ${screenY}px`,
      'transform: translate(-50%, -50%)',
      `font-family: ${FLASH_FONT_FAMILY}`,
      `font-size: ${FLASH_FONT_SIZE}`,
      'font-weight: 800',
      'letter-spacing: 0.04em',
      'color: var(--color-text-primary, #FFFFFF)',
      `text-shadow: ${FLASH_TEXT_SHADOW}`,
      'text-transform: uppercase',
      'white-space: nowrap',
      'pointer-events: none',
      'user-select: none',
    ].join('; ');

    root.appendChild(flashEl);
    nameFlashEl = flashEl;

    // Schedule removal via GSAP registry — NOT setTimeout
    const handle = gsapRegistry.create('tile-effect:name-flash');
    nameFlashHandle = handle;
    setNameFlashActive(true);

    handle.timeline.call(
      () => {
        if (flashEl.parentNode) {
          flashEl.parentNode.removeChild(flashEl);
        }
        // Guard against a newer landing's handle replacing us before callback fires.
        if (nameFlashHandle === handle) {
          nameFlashHandle = null;
        }
        if (nameFlashEl === flashEl) {
          nameFlashEl = null;
        }
        setNameFlashActive(false);
        handle.dispose();
      },
      undefined,
      1.5,
    );
  }

  function formatTileFallbackText(tileType: string): string {
    return tileType
      .split(/[_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // dispose
  // ─────────────────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    unsubscribe();
    floatingPopupHandle.dispose();
    floatingPopupActive = false;

    if (nameFlashHandle) {
      nameFlashHandle.dispose();
      nameFlashHandle = null;
    }
    setNameFlashActive(false);
    syncBoardEffectActivity();
    pendingHazardPopup = null;
    landingContexts.clear();
    seenEventKeys.clear();
    seenEventOrder.length = 0;
    nameFlashEl = null;

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
